import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Calendar, Clock } from "lucide-react";

const DURATION = 5;
const RADIUS = 40;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return "";

  const normalized = timeStr.trim();

  const twelveHourMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelveHourMatch) {
    const [, hour, minute, period] = twelveHourMatch;
    const hourNumber = Number(hour);
    const minuteNumber = Number(minute);

    if (
      Number.isNaN(hourNumber) ||
      Number.isNaN(minuteNumber) ||
      hourNumber < 1 ||
      hourNumber > 12 ||
      minuteNumber < 0 ||
      minuteNumber > 59
    ) {
      return normalized;
    }

    return `${hourNumber}:${String(minuteNumber).padStart(2, "0")} ${period.toUpperCase()}`;
  }

  const twentyFourHourMatch = normalized.match(/^(\d{1,2}):(\d{2})$/);
  if (!twentyFourHourMatch) return normalized;

  const [, hour, minute] = twentyFourHourMatch;
  const hourNumber = Number(hour);
  const minuteNumber = Number(minute);

  if (
    Number.isNaN(hourNumber) ||
    Number.isNaN(minuteNumber) ||
    hourNumber < 0 ||
    hourNumber > 23 ||
    minuteNumber < 0 ||
    minuteNumber > 59
  ) {
    return normalized;
  }

  const period = hourNumber >= 12 ? "PM" : "AM";
  const twelveHour = hourNumber % 12 || 12;
  return `${twelveHour}:${String(minuteNumber).padStart(2, "0")} ${period}`;
};

// Framer Motion Variants for cleaner staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const scaleVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 200, damping: 14 },
  },
};

export default function ThankYouPage({
  isVisible,
  onClose,
  firstName,
  preferredDate,
  preferredTime,
}) {
  const [countdown, setCountdown] = useState(DURATION);

  useEffect(() => {
    if (!isVisible) return;
    setCountdown(DURATION);
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          onClose();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isVisible, onClose]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="thank-you-fullpage"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.4 } }}
          className="fixed inset-0 z-9999 flex flex-col items-center justify-center overflow-hidden bg-white/95 backdrop-blur-sm">
          {/* Animated Background glows */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <motion.div
              animate={{ scale: [1, 1.05, 1], opacity: [0.4, 0.6, 0.4] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-1/2 left-1/2 h-175 w-175 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-100/50 blur-[100px]"
            />
            <motion.div
              animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.7, 0.5] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -top-20 right-0 h-100 w-100 rounded-full bg-blue-100/60 blur-[120px]"
            />
            <motion.div
              animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.6, 0.4] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="absolute -bottom-20 left-0 h-87.5 w-87.5 rounded-full bg-indigo-100/50 blur-[100px]"
            />
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="relative z-10 flex flex-col items-center px-6 text-center">
            {/* Check icon */}
            <motion.div
              variants={scaleVariants}
              className="mb-8 flex h-28 w-28 items-center justify-center rounded-full bg-emerald-100 shadow-sm ring-8 shadow-emerald-200/50 ring-emerald-50">
              <CheckCircle2 size={56} className="text-emerald-600" strokeWidth={2} />
            </motion.div>

            {/* Heading */}
            <motion.h1
              variants={itemVariants}
              className="mb-3 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {firstName ? `Thank you, ${firstName}!` : "Thank you!"}
            </motion.h1>

            <motion.p variants={itemVariants} className="mb-2 text-xl font-semibold text-slate-700">
              Your demo request has been submitted.
            </motion.p>

            <motion.p variants={itemVariants} className="mb-8 max-w-lg text-base text-slate-500">
              Our team will review your request and reach out within{" "}
              <span className="font-semibold text-slate-700">24 hours</span> to confirm your demo
              schedule.
            </motion.p>

            {/* Preferred schedule card - Glassmorphism style */}
            {(preferredDate || preferredTime) && (
              <motion.div
                variants={itemVariants}
                className="mb-10 flex flex-col gap-3 rounded-2xl border border-white/60 bg-white/60 px-8 py-5 text-left shadow-xl shadow-slate-200/40 backdrop-blur-md">
                <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">
                  Preferred Schedule
                </p>
                {preferredDate && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <Calendar size={18} className="shrink-0 text-blue-500" />
                    <span className="font-medium">{formatDate(preferredDate)}</span>
                  </div>
                )}
                {preferredTime && (
                  <div className="flex items-center gap-3 text-slate-700">
                    <Clock size={18} className="shrink-0 text-blue-500" />
                    <span className="font-medium">{formatTime(preferredTime)}</span>
                  </div>
                )}
              </motion.div>
            )}

            {/* Countdown ring + close button */}
            <motion.div variants={itemVariants} className="flex flex-col items-center gap-3">
              <div className="relative h-24 w-24 drop-shadow-sm">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="#f1f5f9" strokeWidth="8" />
                  {/* Smooth animated progress ring */}
                  <motion.circle
                    cx="50"
                    cy="50"
                    r={RADIUS}
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={CIRCUMFERENCE}
                    initial={{ strokeDashoffset: 0 }}
                    animate={{ strokeDashoffset: CIRCUMFERENCE }}
                    transition={{ duration: DURATION, ease: "linear" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-extrabold text-slate-900">{countdown}</span>
                </div>
              </div>

              <p className="text-sm font-medium text-slate-400">
                Closing in{" "}
                <span className="inline-block w-4 text-center text-slate-500">{countdown}</span>s
              </p>

              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(29, 78, 216, 0.5)" }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="mt-2 rounded-xl bg-blue-600 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/30 transition-colors duration-200 cursor-pointer hover:bg-blue-700">
                Close Now
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
